import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import { AlertCircle, CheckCircle } from 'lucide-react';

export function CompleteProfilePage() {
  const { user, completeOAuthProfileRegistration, loading: authLoading } = useAuth();
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false); // Termos devem ser explicitamente aceitos
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!termsAccepted) {
      setError('Você deve aceitar os Termos de Serviço e a Política de Privacidade para continuar.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await completeOAuthProfileRegistration(marketingConsent);
      if (result.success) {
        setSuccess(true);
        // O AuthContext irá setar needsToCompleteProfile para false,
        // e o App.tsx deve redirecionar automaticamente.
        // Mas podemos forçar um redirecionamento após um pequeno delay se necessário,
        // ou apenas mostrar uma mensagem de sucesso.
      } else {
        setError(result.error || 'Ocorreu um erro ao finalizar seu cadastro. Tente novamente.');
      }
    } catch (err: any) {
      setError('Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.');
    } finally {
      setSubmitting(false);
    }
  };

  // Se por algum motivo o usuário não estiver carregado ou não precisar completar o perfil, redirecionar
  // Isso pode acontecer se o estado do AuthContext mudar enquanto esta página está montada.
  // Ou se o usuário tentar acessar esta página diretamente.
  // No entanto, a lógica principal de exibição estará no App.tsx.
  // Se o AuthContext já marcou como completo (success=true), então podemos redirecionar.
  if (success) {
    return <Navigate to="/" replace />;
  }
  
  // Se o auth ainda estiver carregando, ou se o usuário não estiver definido, mostre um loader ou nada.
  // A exibição desta página será controlada pelo App.tsx com base em needsToCompleteProfile.
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className="mx-auto h-12 w-auto"
          src="/logo-boardverso.png"
          alt="Logo Boardverso"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Finalize seu Cadastro
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Olá, {user.full_name || user.username || user.email}! Só mais alguns passos.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative flex items-center" role="alert">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span className="block sm:inline">{error}</span>
              </div>
            )}

            <div>
              <p className="text-sm text-gray-700">
                Para continuar, você precisa aceitar nossos Termos de Serviço e Política de Privacidade.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms-accepted"
                    name="termsAccepted"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms-accepted" className="font-medium text-gray-700">
                    Eu li e aceito os{' '}
                    <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500 underline">
                      Termos de Serviço
                    </Link>{' '}
                    e a{' '}
                    <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500 underline">
                      Política de Privacidade
                    </Link>
                    <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="marketing-consent"
                    name="marketingConsent"
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="marketing-consent" className="font-medium text-gray-700">
                    Eu concordo em receber comunicações de marketing e novidades (Opcional)
                  </label>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={submitting || authLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {submitting ? (
                   <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Salvando...
                  </span>
                ) : 'Concluir Cadastro'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 